import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import MonsterStatBlock from './MonsterStatBlock';
import { useAuth } from '../hooks/useAuth';

function MonsterCreate() {
  const { user, loading: authLoading, signInWithGoogle } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [isBoss, setIsBoss] = useState(false);
  const [hasSpells, setHasSpells] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [monster, setMonster] = useState(null);

  async function handleGenerate(e) {
    e.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setMonster(null);
    try {
      const { data, error: err } = await supabase.functions.invoke('generate-monster', {
        body: { prompt: prompt.trim(), is_boss: isBoss, has_spells: hasSpells },
      });
      if (err) throw err;
      if (data?.error) throw new Error(data.error);
      if (data?.monster) setMonster(data.monster);
      else throw new Error('No monster in response');
    } catch (e) {
      setError(e.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  }

  if (!authLoading && !user) {
    return (
      <section className="section">
        <div className="container">
          <div className="notification is-warning">
            <p className="mb-3">Sign in to create monsters with AI.</p>
            <button onClick={signInWithGoogle} className="button is-primary">
              Sign in with Google
            </button>
            <Link to="/" className="button ml-2">Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="container">
        <h1 className="title">Create Monster (AI)</h1>
        <p className="subtitle">Describe the monster and generate a stat block</p>
        <p className="mb-4 has-text-grey">Generation typically takes around 1 minute. Please be patient.</p>

        <form onSubmit={handleGenerate}>
          <div className="field">
            <label className="label">Describe the monster</label>
            <div className="control">
              <textarea
                className="textarea"
                placeholder="e.g. A fire mage with 100 HP and Fireball"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                disabled={loading}
              />
            </div>
          </div>
          <div className="field">
            <div className="control">
              <label className="checkbox mr-4">
                <input type="checkbox" checked={isBoss} onChange={(e) => setIsBoss(e.target.checked)} />
                {' '}Boss (legendary actions)
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={hasSpells} onChange={(e) => setHasSpells(e.target.checked)} />
                {' '}Has spells
              </label>
            </div>
          </div>
          <div className="field">
            <div className="control">
              <button type="submit" className="button is-primary" disabled={loading || !prompt.trim()}>
                {loading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="notification is-danger mt-4">
            {error}
            <br />
            <small>Ensure generate-monster Edge Function is deployed and OPENAI_API_KEY is set.</small>
          </div>
        )}

        {monster && (
          <div className="mt-5">
            <MonsterStatBlock monster={monster} onSave={() => setMonster(null)} canEdit canSave />
          </div>
        )}
      </div>
    </section>
  );
}

export default MonsterCreate;
